import { useState } from "react";
import { toast } from "@/state/hooks";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Textarea } from "@/ui/components/ui/textarea";
import { Label } from "@/ui/components/ui/label";
import { Card, CardContent } from "@/ui/components/ui/card";
import { useAppState } from "@/state/legacy_module_api";
import { useStore } from "@/state/legacy_module_api";
import { Group as Thread } from "@/data/schemas";
import { groups } from "@/core_bridge/module_api";
import { db as storage } from "@/services/module_api";

type Props = {
  onBack?: () => void;
  onGroupCreated?: (thread: Thread) => void;
};

const CreateGroupPane = ({ onBack, onGroupCreated }: Props) => {
  const { myUserId } = useAppState();
  const setGroup = useStore((s) => s.setGroup);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      setLoading(true);

      // Create group using core API (generates key, encrypts, syncs to server)
      const group = await groups.createGroup(
        name.trim(),
        description.trim() || null,
        myUserId
      );

      // Save to local storage
      await storage.saveThread(group);

      // Add to local store
      setGroup(group as Thread);

      toast.success("Group created!");
      setName("");
      setDescription("");
      onGroupCreated?.(group as Thread);
    } catch (error: any) {
      console.error("Error creating group:", error);
      toast.error(error.message || "Failed to create group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-name="create-group-pane" className="h-full overflow-auto space-y-4 p-4">
      <Card>
        <CardContent className="pt-6">
          {onBack && (
            <Button
              data-name="back-button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <h2 className="text-xl font-semibold mb-4">Create Thread</h2>
          <p className="text-muted-foreground mb-6">
            Create a new thread to communicate with multiple people.
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Thread Name</Label>
              <Input
                data-name="group-name-input"
                id="group-name"
                type="text"
                placeholder="Enter thread name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="group-description">Description (optional)</Label>
              <Textarea
                data-name="group-description-input"
                id="group-description"
                placeholder="Describe your thread"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>

            <Button
              data-name="create-group-submit"
              onClick={handleSubmit}
              disabled={loading || !name.trim()}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Group"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateGroupPane;
